import { Add, TrashCan } from "@carbon/icons-react";
import { Button, Heading, Section, Stack } from "@carbon/react";
import { useEffect } from "react";
import { FormProvider, useFieldArray, UseFormReturn, useWatch } from "react-hook-form";

import { IdentifierFilterableSinglePickerFormField } from "src/components/Pickers/IdentifierFilterableSinglePickerFormField";
import { useT } from "src/internationalization";
import { cn } from "src/utils/string.utils";

import { BlacklistFilterableSinglePickerFormField } from "./BlacklistFilterableSinglePickerFormField";
import { RuleOperatorsFormField } from "./RuleOperatorsFormField";
import { RulePropertyFormField } from "./RulePropertyFormField";
import { RuleTextInputOrMultiSelect } from "./RuleTextInputOrMultiSelect";
import { RuleFormData } from "./useRuleDefinition";

type CreateRuleModalStep1Props = {
  hidden: boolean;
  methods: UseFormReturn<RuleFormData>;
  caseTypeCode: string;
};

export const CreateRuleModalStep1: React.FC<CreateRuleModalStep1Props> = ({
  hidden,
  methods,
  caseTypeCode,
}) => {
  const t = useT();
  const { control } = methods;
  const listType = useWatch({ control, name: "listElementType" });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "criteria",
  });

  useEffect(() => {
    if (listType === null) {
      replace([]);
    }
  }, [listType, replace]);

  return (
    <div
      className={cn("flex flex-col h-full px-5", {
        hidden: hidden,
      })}
    >
      <FormProvider {...methods}>
        <Stack gap={6} className="mb-4">
          {/* Rule Configuration Section */}
          <div className="flex flex-col gap-6">
            <Section level={3}>
              <Heading className="ml-0">
                {t("page.rules.modal.create.form.group.construction.legend")}
              </Heading>
            </Section>
            <IdentifierFilterableSinglePickerFormField
              id="identifier"
              name="identifier"
              label={t("page.rules.modal.create.form.identifier.title")}
              helperText={t("page.rules.modal.create.form.identifier.helper")}
              caseTypeCode={caseTypeCode}
            />
            <BlacklistFilterableSinglePickerFormField
              name="listElementType"
              helperText={t("page.rules.modal.create.form.type.blacklist.helper")}
              label={t("page.rules.modal.create.form.type.blacklist.title")}
              id="blacklist"
            />
          </div>

          {listType && (
            <div className="flex flex-col gap-4">
              <div>
                <div className="text-sm font-semibold mb-1">
                  {t("page.rules.modal.create.form.filters.title")}
                </div>
                <div className="text-sm text-textSecondary">
                  {t("page.rules.modal.create.form.filters.description")}
                </div>
              </div>
              <Stack gap={4}>
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-start gap-3">
                    <div className="flex-[2] max-w-[200px]  min-w-[100px]">
                      <RulePropertyFormField
                        id={field.id}
                        index={index}
                        items={listType.properties}
                        control={control}
                      />
                    </div>
                    <div className="flex-[1.5] max-w-[200px] min-w-[100px]">
                      <RuleOperatorsFormField id={field.id} index={index} methods={methods} />
                    </div>
                    <div className="flex-[2] max-w-md  min-w-[100px]">
                      <RuleTextInputOrMultiSelect index={index} id={field.id} methods={methods} />
                    </div>
                    <div className="pt-6">
                      <Button
                        kind="ghost"
                        size="md"
                        hasIconOnly
                        renderIcon={TrashCan}
                        iconDescription={t("page.rules.modal.create.form.filters.remove")}
                        onClick={() => remove(index)}
                      />
                    </div>
                  </div>
                ))}

                {/* Add filter button */}
                <Button
                  kind="tertiary"
                  size="sm"
                  renderIcon={Add}
                  onClick={() => append({ property: null, operator: null, value: "" })}
                >
                  {t("page.rules.modal.create.form.filters.add")}
                </Button>
              </Stack>
            </div>
          )}
        </Stack>
      </FormProvider>
    </div>
  );
};
